// components
import { Typography } from '@mui/material';

// hooks
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';

// styles
import {
  ActionsContainer,
  Container,
  Section,
  TimeSlotIconButton
} from './TimeSlotActions.styles';

// utils
import dayjs from 'dayjs';
import { useMemo } from 'react';

export const TimeSlotActions = () => {
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  const { timeSlotMin, timeSlotMax, setTimeSlotMinMax } = useCalendarStore(
    useShallow((state) => ({
      setTimeSlotMinMax: state.control.setTimeSlotMinMax,
      timeSlotMin: state.timeSlotMin,
      timeSlotMax: state.timeSlotMax
    }))
  );

  const handleStartTimeChange = (type: 'add' | 'subtract') => {
    const minDate = dayjs(timeSlotMin);
    setTimeSlotMinMax(
      'min',
      minDate[type](1, 'hour').set('seconds', 0).toDate()
    );
  };
  const handleEndTimeChange = (type: 'add' | 'subtract') => {
    const maxDate = dayjs(timeSlotMax);
    setTimeSlotMinMax(
      'max',
      maxDate[type](1, 'hour').set('seconds', 0).toDate()
    );
  };

  const disableButtons = useMemo(() => {
    const min = dayjs(timeSlotMin);
    const max = dayjs(timeSlotMax);

    return {
      min: {
        subtract: min.hour() == 0,
        add: min.hour() + 1 >= max.hour()
      },
      max: {
        subtract: max.hour() - 1 <= min.hour(),
        add: max.hour() == 23
      }
    };
  }, [timeSlotMin, timeSlotMax]);

  const renderActions = (type: 'min' | 'max') => {
    const date = type == 'min' ? timeSlotMin : timeSlotMax;
    return (
      <Section>
        <Typography variant="subtitle2" fontSize="medium">
          {type == 'min' ? t('startTime') : t('endTime')}
        </Typography>
        <ActionsContainer>
          <TimeSlotIconButton
            type="minus"
            onClick={(type == 'min'
              ? handleStartTimeChange
              : handleEndTimeChange
            ).bind(null, 'subtract')}
            disabled={disableButtons[type].subtract}
          />
          <Typography>{date ? dayjs(date).format('HH:mm') : '-'}</Typography>
          <TimeSlotIconButton
            type="plus"
            onClick={(type == 'min'
              ? handleStartTimeChange
              : handleEndTimeChange
            ).bind(null, 'add')}
            disabled={disableButtons[type].add}
          />
        </ActionsContainer>
      </Section>
    );
  };

  return (
    <Container>
      {renderActions('min')}
      {renderActions('max')}
    </Container>
  );
};

export default TimeSlotActions;
